import React, {useEffect, useMemo, useState} from 'react'
import {DeleteOutlined, ExclamationCircleOutlined} from '@ant-design/icons'
import {Input, LiveSearch, CustomSelect, CustomModal} from '../../../common'
import {i18n} from '../../../../i18n'
import {sharedStyles} from '../../../../style/shared_styles'
import {Button} from 'antd'
import {ButtonStyle} from '../../../../style/button'
import EmptyDiv from '../../../common/EmptyDiv'
import {InputLabelProps} from '../../../common/InputLabel'
import {InputLabel} from '../../../common/index'
import {ConfigTypes, SearchTypes} from '../../../../utils/enums'
import styles, {elemMargin} from '../../styles/TimeDoseTable.style'
import {IngredientSearchEntry, MedicationSearchEntry, TherapyMedicationDto, OptionItem} from '../../../../utils/types'
import {Color} from '../../../../style/custom/colors'
import {Format} from '../../../../utils/constants'
import {
    checkAllowedMultiplier,
    getTimeDoseArrays,
    getTimeDoseObj,
    hasAnyValidMedicationDto,
    prepareDosageForDisplay
} from '../../../../utils/medication_helper'
import {
    convertFloatNumber,
    generateNumberArray,
    isNumber,
    matchRegex,
    TIME_REGEX,
    toLocaleStringCustom,
    buildSelectOptionsObject
} from '../../../../utils/helper'
import {addElement} from '../../../../utils/array_helper'
import {toNumber} from '../../../../utils/observation_helper'
import {useStores} from '../../../../store'
import {observer} from 'mobx-react'
import EmptyColumnHeader from './EmptyColumnHeader'

const initialTimes = ['']

/**
 * Component for setting the dosage and time of each med from the therapy medication.
 */
const TimeDoseTable: React.FC<TimeDoseTableProps> = observer(props => {
    const [times, setTimes] = useState<string[]>([])
    const [meds, setMeds] = useState<(MedicationSearchEntry | string)[]>([''])
    const [medsOrUnits, setMedsOrUnits] = useState<(MedicationSearchEntry | string)[]>([''])
    const [doses, setDoses] = useState<number[][]>([[]])
    const [totalDosage, setTotalDosage] = useState(0)
    const [dosesErr, setDosesErr] = useState<string[][]>([[]])
    const [units, setUnits] = useState<string[]>([])
    const [showMaxTimesDialog, setShowMaxTimesDialog] = useState(false)

    const {userStore} = useStores()

    // Initialize prefilled data
    useEffect(() => {
        if (props.therapyMedicationList) {
            const {timeArr, medArr, medUnitArr, doseArr, doseErr, unitArr} = getTimeDoseArrays(props.therapyMedicationList)
            setTimes(timeArr)
            setMeds(medArr)
            setMedsOrUnits(medUnitArr)
            setDoses(doseArr)
            setDosesErr(doseErr)
            setUnits(unitArr)
        } else {
            setTimes(initialTimes)
        }
    }, [])

    // Calculates total dosage
    useEffect(() => {
        let total = 0
        meds.forEach((med, medIndex) => {
            const medDosage = typeof med === 'string' ? toNumber(med) || 0 : (med.originallyHadDosage ? med.dosage : toNumber(med.uiDosage)) || 0
            times.forEach((time, timeIndex) => {
                const dosage = toNumber(doses[medIndex][timeIndex]?.toString()) || 0
                total += dosage * medDosage
            })
        })
        setTotalDosage(total)
    }, [meds, doses])

    const onTimeValueChange = (index: number) => (val: string) => {
        // console.log('onTimeValueChange to', val)
        // magic of midnight... 🧚‍♀️
        const adjustedVal = val === '24:00' ? '23:59' : val;
        const newTimes = [...times]
        newTimes.splice(index, 1, adjustedVal)
        setTimes(newTimes)
        // console.log('times after onTimeValueChange', newTimes)
    }

    const deleteIntakeTime = (idx: number) => {
        // delete the column
        const newTimes = [...times]
        newTimes.splice(idx, 1)
        setTimes(newTimes)
        // remove the column dosage values
        const newDoses = [...doses]
        const newDosesErr = [...dosesErr]
        const newUnits = [...units]
        meds.forEach((med, medIndex) => {
            newDoses[medIndex].splice(idx, 1)
            newDosesErr[medIndex].splice(idx, 1)
        })
        setDoses(newDoses)
        setDosesErr(newDosesErr)
        setUnits(newUnits)
    }

    const addIntakeTime = () => {
        if (times.length >= 4) {
            setShowMaxTimesDialog(true)
            return
        }
        // adds the column
        const newTimes = [...times]
        newTimes.push('')
        setTimes(newTimes)
        // since the line exists dose[line][col] will not crash, no need to add anything to doses
    }

    const deleteMedicine = (idx: number) => {
        // delete the line
        const newMeds = [...meds]
        const deleted = newMeds.splice(idx, 1)
        setMeds(newMeds)
        const newMedsOrUnits = [...medsOrUnits]
        const deleted2 = newMedsOrUnits.splice(idx, 1)
        setMedsOrUnits(newMedsOrUnits)
        // remove the line dosage values
        const newDoses = [...doses].filter((arr, index) => index !== idx)
        setDoses(newDoses)
        const newDoseErr = [...dosesErr].filter((arr, index) => index !== idx)
        setDosesErr(newDoseErr)
        // remove unit
        const newUnits = [...units].filter((arr, index) => index !== idx)
        setUnits(newUnits)
        // delete disabled data in parent component if this was the last medication
        deleteDataIfLast(deleted[0], newMeds)
        deleteDataIfLast(deleted2[0], newMedsOrUnits)
    }

    const addMedicine = () => {
        // adds the line
        const newMeds = [...meds]
        newMeds.push('')
        setMeds(newMeds)
        console.log('newMeds', newMeds)
        
        const newMedsOrUnits = [...medsOrUnits]
        newMedsOrUnits.push('')
        setMedsOrUnits(newMedsOrUnits)
        console.log('newMedsOrUnits', newMedsOrUnits)

        // since the line did not previously exist, any attempt to add to doses[newLine][x] would crash without init to []
        const newDoses = [...doses]
        newDoses.push([])
        setDoses(newDoses)

        const newDoseErr = [...dosesErr]
        newDoseErr.push([])
        setDosesErr(newDoseErr)

        const newUnits = [...units]
        newUnits.push('')
        setUnits(newUnits)
    }
    // could be string when med is deleted: val = ""
    const onMedicineChange = (index: number) => (val: MedicationSearchEntry | string) => {
        if (typeof val !== 'string') {
            props.setMedicationChanged?.(val)
        }
        updateMedsList(index, val)
        updateMedsOrUnitsList(index, val)
    }

    /**
     * change medication dosage for anonymous medication
     * (does not have an medicationDTO, only ingredient is selected)
     * @param index
     */
    const onMedicationDosageChange = (index: number) => (newDosage: string) => {
        const currentMedication = meds?.[index]

        // if current medication does not have dosage
        if (currentMedication && typeof currentMedication !== 'string') {
            currentMedication.uiDosage = newDosage
        }
        const replacedMedOrDosage = typeof currentMedication !== 'string' ? currentMedication : newDosage?.toString()

        updateMedsList(index, replacedMedOrDosage)
    }

    /** Handler für Auswahl Medikamentendosierungseinheit */
    const onUnitChange = (index: number) => (newUnit: string) => {
        console.log('onUnitChange');
        console.log('index:', index);
        console.log('newUnit:', newUnit);
        const currentMedication = medsOrUnits?.[index]
        console.log('currentMedication before change:', currentMedication);

        // if current medication does not have dosage
        if (currentMedication && typeof currentMedication !== 'string') {
            currentMedication.unit = newUnit
            console.log('hit if current medication does not have dosage');
        }
        const replacedMedOrDosage = typeof currentMedication !== 'string' ? currentMedication : newUnit?.toString()
        console.log('replacedMedOrDosage:', replacedMedOrDosage);

        updateMedsOrUnitsList(index, replacedMedOrDosage)
    }
    
    /**
     * convert current value dosage to a valid float number (if not valid, keep the value and show error on submit)
     * @param index
     */
    const onMedicationDosageBlur = (index: number) => (newDosage: string) => {
        const currentMedication = meds?.[index]

        const convertedDosage: number | undefined = convertFloatNumber(newDosage)
        // if dosage is missing from med
        if (typeof currentMedication !== 'string') {
            currentMedication.dosage = convertedDosage || null
            currentMedication.uiDosage = toLocaleStringCustom(convertedDosage, i18n.locale) || newDosage
            props.setMedicationChanged?.(currentMedication)
            updateMedsList(index, currentMedication)
        } else {
            updateMedsList(index, toLocaleStringCustom(convertedDosage, i18n.locale) || newDosage)
        }
    }

    const updateMedsList = (index: number, val: MedicationSearchEntry | string) => {
        const newMeds = [...meds]
        const deleted = newMeds.splice(index, 1, val)
        setMeds(newMeds)
        deleteDataIfLast(deleted[0], newMeds)
    }

    const updateMedsOrUnitsList = (index: number, val: MedicationSearchEntry | string) => {
        // console.log('updateMedsOrUnitsList before', medsOrUnits)
        const newMedsOrUnits = [...medsOrUnits]
        const deleted = newMedsOrUnits.splice(index, 1, val)
        setMedsOrUnits(newMedsOrUnits)
        deleteDataIfLast(deleted[0], newMedsOrUnits)
        // console.log('updateMedsOrUnitsList after', newMedsOrUnits)
    }

    const getFirstSelectedUnit = (): string | undefined => {
        for (const item of medsOrUnits) {
            // If the item is a string, return it (assuming it's a unit)
            if (typeof item === 'string') {
                return item;
            }
            else
            {
                return item.unit;
            }
        }
        // If no unit is found, return undefined
        return undefined;
    }

    // if meds changes, set the unit of the last medication from list
    useEffect(() => {
        const unit = getFirstSelectedUnit();
        props.onChangeUnit?.(unit || null)
    }, [medsOrUnits])

    const deleteDataIfLast = (deleted: MedicationSearchEntry | string, medications: (MedicationSearchEntry | string)[]) => {
        const isTheLastSelectedMedication = !medications.some(x => typeof x !== 'string')
        const isEmptyList = medications.length === 0
        if (isEmptyList || (typeof deleted !== 'string' && isTheLastSelectedMedication)) {
            props.setMedicationChanged?.(null)
        }
    }

    const onDosageChange = (medIndex: number, timeIndex: number) => (value: number) => {
        const newDoses = [...doses]
        newDoses[medIndex][timeIndex] = value
        setDoses(newDoses)
    }

    const onDosageBlur = (medIndex: number, timeIndex: number) => {
        const toValidate = doses[medIndex][timeIndex]
        const isValid = checkAllowedMultiplier(toValidate)
        const newDoseErr = [...dosesErr]
        newDoseErr[medIndex][timeIndex] = isValid ? '' : 'errored'
        setDosesErr(newDoseErr)
    }

    useEffect(() => {
        if (props.triggerValidate) {
            onValidate()
        }
    }, [props.triggerValidate])

    const onValidate = () => {
        let errMessages: string[] = []
        const timeErrorMessage = i18n.t('editMed.error.time')
        const medErrorMessage = i18n.t('editMed.error.medication')
        const emptyRowColumnErrorMessage = i18n.t('editMed.error.emptyRowColumn')
        const doseFormatErrorMessage = i18n.t('editMed.error.multiplierFormat')

        times.forEach((time, timeIndex) => {
            if (!matchRegex(time, TIME_REGEX)) {
                errMessages = addElement(errMessages, timeErrorMessage)
            }
            // check if have values per column
            let hasOneValid = false
            doses.forEach(intakeList => {
                if (toNumber(intakeList[timeIndex]?.toString())) {
                    hasOneValid = true
                }
            })
            if (!hasOneValid) {
                errMessages = addElement(errMessages, emptyRowColumnErrorMessage)
            }
        })

        // check if have empty row
        meds.forEach((med, medIndex) => {
            const dosage = typeof med === 'string' ? med : med.dosage
            if (!convertFloatNumber(dosage) || dosage! <= 0) {
                errMessages = addElement(errMessages, medErrorMessage)
            }
            if (!doses[medIndex] || doses[medIndex].map(it => toNumber(it?.toString())).filter(it => isNumber(it)).length === 0) {
                errMessages = addElement(errMessages, emptyRowColumnErrorMessage)
            }
        })

        // same for unit
        medsOrUnits.forEach((medOrUnit, medIndex) => {
            const unit = typeof medOrUnit === 'string' ? medOrUnit : medOrUnit.unit
            // TODO: Fehler wenn leer?
            // if (!convertFloatNumber(dosage) || dosage! <= 0) {
            //     errMessages = addElement(errMessages, medErrorMessage)
            // }
            // if (!doses[medIndex] || doses[medIndex].map(it => toNumber(it?.toString())).filter(it => isNumber(it)).length === 0) {
            //     errMessages = addElement(errMessages, emptyRowColumnErrorMessage)
            // }
        })

        if (dosesErr.some(row => row.some(el => !!el))) {
            errMessages = addElement(errMessages, doseFormatErrorMessage)
        }
        let therapyMedications: TherapyMedicationDto[] = []
        if (errMessages.length === 0) {
            // filter by valid intake
            const numberMultipliers = doses.map(row => row.map(el => toNumber(el?.toString()) as number)) // safe cast since it was checked previously
            therapyMedications = getTimeDoseObj(times, meds as MedicationSearchEntry[], numberMultipliers, medsOrUnits, props.ingredients)
        }

        props.onValidate?.(therapyMedications, errMessages)
    }

    const unit = props.unit ? userStore.getI18nText(ConfigTypes.UNIT_MEASURE, props.unit) : '-'
    const unitsOption: OptionItem[] = buildSelectOptionsObject(userStore.getUnitMeasure)

    const isStrict = useMemo(() => {
        return hasAnyValidMedicationDto(meds)
    }, [meds])

    return (
        <div>
            <div style={sharedStyles.inline}>
                <div style={styles.column1}>
                    <EmptyColumnHeader />
                    <EmptyDiv inputHeight={true} />
                    <EmptyDiv style={styles.columnHeader} />
                    {meds.map((med, index) => (
                        <div key={`x${index}${med}`} style={{...styles.center, marginTop: elemMargin / 2}}>
                            <DeleteOutlined onClick={() => deleteMedicine(index)} />
                        </div>
                    ))}
                </div>

                <div style={styles.column2}>
                    <EmptyColumnHeader />
                    <EmptyDiv inputHeight={true} />
                    <InputLabel label={i18n.t('editMed.medicationLabel')} />
                    {generateNumberArray(meds.length).map(index => (
                        <div key={`${index}${meds[index]}`} style={{marginTop: elemMargin / 2}}>
                            <LiveSearch
                                searchType={SearchTypes.product}
                                placeholder={i18n.t('editMed.medicationPlaceholder')}
                                onValueChanged={onMedicineChange(index)}
                                searchOptions={{filter: props.ingredients?.map(i => i.id) || [], strict: isStrict}}
                                defaultValue={typeof meds[index] === 'string' ? undefined : meds[index]}
                                hideErrorField={true}
                                searchThreshold={4}
                                skipInitialize={true}
                            />
                        </div>
                    ))}
                </div>

                <div style={styles.column3}>
                    <EmptyColumnHeader />
                    <EmptyDiv inputHeight={true} />
                    <InputLabel label={i18n.t('editMed.dose')} mandatory={true} />
                    {meds.map((med, index) => {
                        const {id, val} =
                            typeof med === 'string'
                                ? {id: `empty${index}`, val: med}
                                : {
                                      id: med.id,
                                      val: (med.originallyHadDosage ? med.dosage?.toLocaleString(i18n.locale) : med.uiDosage) || ''
                                  }
                        return (
                            <div key={id} style={{marginTop: elemMargin / 2}}>
                                <Input
                                    value={val}
                                    onValueChanged={onMedicationDosageChange(index)}
                                    onBlur={e => onMedicationDosageBlur(index)(e.target.value)}
                                    min={0}
                                    disabled={(typeof med !== 'string' && !!med.originallyHadDosage)} // disabled if medication is selected and has unit
                                    hideErrorField={true}
                                    skipInitialize={true}
                                    type={'number'}
                                />
                            </div>
                        )
                    })}
                </div>

                <div style={styles.column4}>
                    {/* <EmptyDiv style={styles.columnHeader} /> */}
                    {/* <div style={{...sharedStyles.inlineContainer, ...styles.columnHeader}}>
                        <InputLabel label={'col4'} />
                    </div> */}
                    <EmptyColumnHeader />
                    <EmptyDiv inputHeight={true} />
                    <InputLabel label={i18n.t('editMed.unit')} mandatory={true} />
                    {medsOrUnits.map((med, index) => {
                        const {id, val} =
                            typeof med === 'string'
                                ? {id: `empty${index}`, val: med}
                                : {
                                      id: med.id,
                                      val: med.unit || ''
                                  }
                        return (
                            <div key={id} style={{marginTop: elemMargin / 2}}>
                                <CustomSelect
                                    hideLabelField={true}
                                    hideErrorField={true}
                                    items={unitsOption}
                                    value={userStore.getI18nText(ConfigTypes.UNIT_MEASURE, val)}
                                    disabled={(typeof med !== 'string' && !!med.originallyHadUnit)} // disabled if medication is selected and has unit
                                    onValueChanged={onUnitChange(index)}/>
                            </div>
                        )
                    })}
                </div>

                {times.map((time, timeIdx) => (
                    <div key={`header${timeIdx}`} style={styles.column5}>
                        <div style={{...sharedStyles.inlineContainer, ...styles.columnHeader}}>
                            <InputLabel label={i18n.t('editMed.intakeTime', {index: timeIdx + 1})} mandatory={true} />
                            <DeleteOutlined onClick={() => deleteIntakeTime(timeIdx)} style={{marginRight: elemMargin}} />
                        </div>
                        {/* <div> */}
                            <Input
                                key={`val${timeIdx}`}
                                value={time === '23:59' ? '24:00' : time}
                                placeholder={'HH:MM'}
                                onValueChanged={onTimeValueChange(timeIdx)}
                                hideErrorField={true}
                            />
                            {time === '00:00' && (
                                <div style={{color: Color.warningYellow, fontSize: 11, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4}}>
                                    <ExclamationCircleOutlined />
                                    <span>{i18n.t('editMed.warning.midnight')}</span>
                                </div>
                            )}
                        {/* </div> */}
                        <InputLabel label={i18n.t('editMed.amount')} mandatory={true} />
                        {meds.map((med, medIdx) => (
                            <div key={`${medIdx}${timeIdx}`} style={{marginTop: elemMargin / 2}}>
                                <Input
                                    value={toLocaleStringCustom(doses[medIdx][timeIdx], i18n.locale) || doses[medIdx][timeIdx]}
                                    placeholder={'0'}
                                    onValueChanged={onDosageChange(medIdx, timeIdx)}
                                    onBlur={() => onDosageBlur(medIdx, timeIdx)}
                                    error={dosesErr[medIdx][timeIdx]}
                                    hideErrorField={true}
                                    type={'number'}
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div style={sharedStyles.inlineContainer}>
                <Button type={'default'} shape={'round'} style={ButtonStyle.primary} onClick={addMedicine}>
                    {i18n.t('button.addDose')}
                </Button>
                <Button type={'default'} shape={'round'} style={ButtonStyle.primary} onClick={addIntakeTime}>
                    {i18n.t('button.addIntakeTime')}
                </Button>
            </div>

            {/* Dosage display */}
            <div style={{...sharedStyles.inline, background: Color.secondary3, padding: 10}}>
                <div style={{...sharedStyles.oneColumn, alignItems: 'center'}}>
                    <span style={{fontSize: 17}}>{i18n.t('editMed.dailyDose')}</span>
                    <span
                        style={{
                            fontSize: 20,
                            fontWeight: 'bold'
                        }}>
                        {totalDosage >= 0 ? `${prepareDosageForDisplay(totalDosage)} ${getFirstSelectedUnit() || ''}` : '-'}
                    </span>
                </div>
            </div>

            <CustomModal open={showMaxTimesDialog} title={i18n.t('editMed.error.hint')}>
                <div style={{...sharedStyles.column, alignItems: 'center'}}>
                    <span style={{fontSize: 18, textAlign: 'center'}}>{i18n.t('editMed.error.maxIntakeTimes')}</span>
                    <Button type={'default'} shape={'round'} style={{...ButtonStyle.primary, marginTop: 25}} onClick={() => setShowMaxTimesDialog(false)}>
                        {i18n.t('button.cancel')}
                    </Button>
                </div>
            </CustomModal>
        </div>
    )
})

type TimeDoseTableProps = {
    therapyMedicationList?: TherapyMedicationDto[]
    dailyDose?: number
    ingredients?: IngredientSearchEntry[]
    setMedicationChanged?: (selectedMedication: MedicationSearchEntry | null) => void
    triggerValidate: boolean
    onValidate?: (therapyMedications: TherapyMedicationDto[], errorMessages: string[]) => void
    unit?: string
    onChangeUnit?: (unit: string | null) => void
}

export default TimeDoseTable
